import React, { useEffect, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import {
  fetchProjectsByResource,
  fetchLinkableProjects,
  addResourceToProject,
  removeResourceFromProject,
} from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import {
  PiPlusLight,
  PiProjectorScreenChartLight,
  PiXLight,
  PiLinkBreakLight,
} from 'react-icons/pi';

// Format a backend resource type token for display.
//   "review_session" → "Review"
//   "research_chat"  → "Research Chat"
//   "data_source"    → "Data Source"
const formatResourceType = (raw: string): string => {
  if (!raw) return '';
  const lower = String(raw).trim().toLowerCase();
  if (lower === 'review_session') return 'Review';
  return lower
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

interface ProjectBadgesProps {
  resourceType: string;
  resourceUuid: string;
}

const ProjectBadges: React.FC<ProjectBadgesProps> = ({ resourceType, resourceUuid }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [projects, setProjects] = useState<any[]>([]);
  const [linkableProjects, setLinkableProjects] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!resourceType || !resourceUuid) return;
    const result = await dispatch(fetchProjectsByResource(resourceType, resourceUuid));
    if (result && Array.isArray(result)) {
      setProjects(result);
    }
    setIsLoaded(true);
  }, [dispatch, resourceType, resourceUuid]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleShowDropdown = async () => {
    if (showDropdown) {
      setShowDropdown(false);
      return;
    }
    const result = await dispatch(fetchLinkableProjects());
    if (result && Array.isArray(result)) {
      // Filter out already-linked projects
      const linkedUuids = new Set(projects.map((p) => p.uuid));
      setLinkableProjects(result.filter((p: any) => !linkedUuids.has(p.uuid)));
    }
    setShowDropdown(true);
  };

  const handleLink = async (projectUuid: string) => {
    setIsLinking(true);
    await dispatch(addResourceToProject(projectUuid, resourceType, resourceUuid));
    setShowDropdown(false);
    setIsLinking(false);
    loadProjects();
  };

  const handleUnlink = (projectUuid: string, projectTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const typeLabel = formatResourceType(resourceType);
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Unlink {{type}}', { type: typeLabel }),
        subMsg: t(
          'Are you sure you want to unlink this {{type}} from "{{project}}"? Collaborators may lose any access granted through this project.',
          { type: typeLabel.toLowerCase(), project: projectTitle }
        ),
        icon: <PiLinkBreakLight className="w-6 h-6 text-red-600" />,
        isDest: true,
        onConfirm: async () => {
          try {
            await dispatch(removeResourceFromProject(projectUuid, resourceType, resourceUuid));
            await loadProjects();
          } finally {
            dispatch(hideModal());
          }
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  // Show a small skeleton placeholder while loading
  if (!isLoaded) {
    return (
      <div className="flex items-center gap-1.5">
        <PiProjectorScreenChartLight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
        <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-md animate-fadePulse" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap relative">
      <PiProjectorScreenChartLight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      {(projects.length < 3 ? projects : projects.slice(0, 2)).map((project) => (
        <button
          key={project.uuid}
          onClick={() => history.push(`/projects/${project.uuid}`)}
          className="group inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors cursor-pointer"
        >
          {project.title}
          <PiXLight
            className="w-3 h-3 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
            onClick={(e) => handleUnlink(project.uuid, project.title, e)}
          />
        </button>
      ))}
      {projects.length >= 3 && (
        <div className="relative group/overflow">
          <button className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-md bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer">
            +{projects.length - 2} {t('more')}
          </button>
          <div className="hidden group-hover/overflow:block absolute top-full left-0 mt-1 z-50 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
            {projects.slice(2).map((project) => (
              <div key={project.uuid} className="flex items-center justify-between px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 group/item">
                <button
                  onClick={() => history.push(`/projects/${project.uuid}`)}
                  className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1 text-left"
                >
                  {project.title}
                </button>
                <PiXLight
                  className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover/item:opacity-60 hover:!opacity-100 cursor-pointer flex-shrink-0 ml-2 transition-opacity"
                  onClick={(e) => handleUnlink(project.uuid, project.title, e)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="relative">
        <button
          onClick={handleShowDropdown}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] rounded-md text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <PiPlusLight className="w-3 h-3" />
          {projects.length === 0 && t('Add to project')}
        </button>
        {showDropdown && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
            <div className="absolute top-full left-0 mt-1 z-50 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
              {linkableProjects.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 px-3 py-2">{t('No projects available')}</p>
              ) : (
                linkableProjects.map((p: any) => (
                  <button
                    key={p.uuid}
                    onClick={() => handleLink(p.uuid)}
                    disabled={isLinking}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50"
                  >
                    {p.title}
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectBadges;
